'use client';

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { cn } from '@/lib/utils';
import { isWinnerlessRow } from '@/draw/core/matchOutcome';
import type { DrawMatch } from '@/draw/types';
import { feederSections, roundGameSlots, thirdPlaceFeeder, type Ladder, type LadderMatch, type LadderRound } from '../libs/ladderModel';
import { displayMatchCode } from '@/app/[locale]/event/[eventId]/libs/matchCode';
import { useLadderText } from '../libs/ladderText';
import { useDrawHighlight } from './DrawHighlight';
import { MatchCard } from './MatchCard';

const PHONE_HIDE_UNSELECTED = '@max-[639px]:[&:not([data-selected])]:hidden';

// Tailwind only emits complete literal class strings, so each tier spells its variants out.
// The ladder switches from the scrolling tree to the full tree once its container fits
// N round columns + gaps (28px); the last column ends flush, with no space reserved after it. Three to six round columns stop at 384px
// (max-w-96) and the two-round tier stops at 288px, so a short ladder stays a compact tree instead of stretching cards across the page.
const TIERS = {
  // Two rounds always fit from 640px, so there is no scroller band.
  2: {
    column: '@min-[640px]:min-w-44 @min-[640px]:max-w-72 @min-[640px]:flex-1',
    header: '@min-[640px]:text-center',
    viewport: '',
    mask: '',
    list: '@min-[640px]:w-full',
    dots: '',
  },
  3: {
    column:
      '@min-[640px]:@max-[859px]:w-[calc((100cqw-3.5rem)/2.3)] @min-[640px]:@max-[859px]:snap-start @min-[860px]:min-w-44 @min-[860px]:max-w-96 @min-[860px]:flex-1',
    header: '@min-[860px]:text-center',
    viewport: '@min-[640px]:@max-[859px]:snap-x @min-[640px]:@max-[859px]:snap-mandatory',
    mask: '@min-[640px]:@max-[859px]:[mask-image:linear-gradient(to_right,black_78%,rgb(0_0_0/0.25))]',
    list: '@min-[860px]:w-full',
    dots: '@min-[640px]:@max-[859px]:flex',
  },
  4: {
    column:
      '@min-[640px]:@max-[919px]:w-[calc((100cqw-3.5rem)/2.3)] @min-[640px]:@max-[919px]:snap-start @min-[920px]:min-w-40 @min-[920px]:max-w-96 @min-[920px]:flex-1',
    header: '@min-[920px]:text-center',
    viewport: '@min-[640px]:@max-[919px]:snap-x @min-[640px]:@max-[919px]:snap-mandatory',
    mask: '@min-[640px]:@max-[919px]:[mask-image:linear-gradient(to_right,black_78%,rgb(0_0_0/0.25))]',
    list: '@min-[920px]:w-full',
    dots: '@min-[640px]:@max-[919px]:flex',
  },
  5: {
    column:
      '@min-[640px]:@max-[1179px]:w-[calc((100cqw-3.5rem)/2.3)] @min-[640px]:@max-[1179px]:snap-start @min-[1180px]:min-w-44 @min-[1180px]:max-w-96 @min-[1180px]:flex-1',
    header: '@min-[1180px]:text-center',
    viewport: '@min-[640px]:@max-[1179px]:snap-x @min-[640px]:@max-[1179px]:snap-mandatory',
    mask: '@min-[640px]:@max-[1179px]:[mask-image:linear-gradient(to_right,black_78%,rgb(0_0_0/0.25))]',
    list: '@min-[1180px]:w-full',
    dots: '@min-[640px]:@max-[1179px]:flex',
  },
  6: {
    column:
      '@min-[640px]:@max-[1379px]:w-[calc((100cqw-3.5rem)/2.3)] @min-[640px]:@max-[1379px]:snap-start @min-[1380px]:min-w-44 @min-[1380px]:max-w-96 @min-[1380px]:flex-1',
    header: '@min-[1380px]:text-center',
    viewport: '@min-[640px]:@max-[1379px]:snap-x @min-[640px]:@max-[1379px]:snap-mandatory',
    mask: '@min-[640px]:@max-[1379px]:[mask-image:linear-gradient(to_right,black_78%,rgb(0_0_0/0.25))]',
    list: '@min-[1380px]:w-full',
    dots: '@min-[640px]:@max-[1379px]:flex',
  },
} as const;

const tierFor = (rounds: number) => TIERS[Math.min(6, Math.max(2, rounds)) as 2 | 3 | 4 | 5 | 6];

const isRealPlayer = (p: DrawMatch['player1']) => !!p && !p.isBye;
const endedWithoutWinner = (m: DrawMatch) =>
  m.status === 'completed' &&
  !m.winner &&
  isWinnerlessRow({ outcome: m.outcome, is_walkover: m.isWalkover });
// A team has left this match along its line: a decided winner, or the lone team facing a BYE
// (a plate bye can carry no stored winner). A double withdrawal sends nobody.
const sentWinner = (m: DrawMatch) =>
  !(m.player1?.isBye && m.player2?.isBye) &&
  ((!!m.winner && (m.status === 'completed' || m.isBye === true || m.isWalkover === true)) ||
  (isRealPlayer(m.player1) !== isRealPlayer(m.player2) &&
    (!!m.player1?.isBye || !!m.player2?.isBye)));

const holdsMatch = (ladder: Ladder, matchId: string): boolean =>
  ladder.rounds.some(
    (r) =>
      r.matches.some((lm) => lm.match.matchId === matchId) ||
      r.feeders.some((f) => holdsMatch(f, matchId)),
  );

interface LadderViewProps {
  ladder: Ladder;
  playerHref?: (id: string) => string | undefined;
  nested?: boolean;
}

export function LadderView({ ladder, playerHref, nested = false }: LadderViewProps) {
  const t = useTranslations('Event.drawView.ladder');
  const text = useLadderText();
  const highlight = useDrawHighlight();
  const [chosen, setChosen] = useState<number | null>(null);
  const selected = Math.min(chosen ?? ladder.currentRound, Math.max(0, ladder.rounds.length - 1));
  const tier = tierFor(ladder.rounds.length);
  const third = nested ? null : thirdPlaceFeeder(ladder);
  const lastIndex = ladder.rounds.length - 1;

  const viewport = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ left: false, right: false, first: 0 });
  const step = useCallback(() => {
    const col = viewport.current?.querySelector<HTMLElement>('li[data-round-index]');
    return col ? col.offsetWidth + 28 : 0;
  }, []);
  const measure = useCallback(() => {
    const vp = viewport.current;
    if (!vp) return;
    const max = vp.scrollWidth - vp.clientWidth;
    const s = step();
    const left = vp.scrollLeft > 4;
    const right = vp.scrollLeft < max - 4;
    const first = s ? Math.round(vp.scrollLeft / s) : 0;
    setEdges((p) =>
      p.left === left && p.right === right && p.first === first ? p : { left, right, first },
    );
  }, [step]);

  // Tablet: bring the selected round to the left edge (no-op when nothing overflows).
  useEffect(() => {
    const vp = viewport.current;
    const col = vp?.querySelector<HTMLElement>(`li[data-round-index="${selected}"]`);
    if (vp && col && typeof vp.scrollTo === 'function')
      vp.scrollTo({ left: col.offsetLeft, behavior: chosen === null ? 'auto' : 'smooth' });
    measure();
  }, [selected, chosen, measure]);

  // Re-measure when the container resizes (rotation, sidebar) so arrows/dots stay truthful.
  useEffect(() => {
    const vp = viewport.current;
    if (!vp || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(vp);
    return () => ro.disconnect();
  }, [measure]);

  // A jump to a match in this ladder (or a playoff under one of its rounds) opens that round,
  // so on phones the target card is not hidden behind another round tab. A layout effect: the
  // round choice commits synchronously with the jump, before the draw view schedules its landing.
  // A plain effect's update is a default-priority render that can still be mid-flight (or not yet
  // committed) when the landing frame fires, and focus() on a still-hidden card does nothing.
  const focusId = highlight?.focus?.matchId;
  const focusN = highlight?.focus?.n;
  useLayoutEffect(() => {
    if (!focusId) return;
    if (third && holdsMatch(third, focusId)) {
      setChosen(ladder.rounds.length - 1);
      return;
    }
    const i = ladder.rounds.findIndex(
      (r) =>
        r.matches.some((lm) => lm.match.matchId === focusId) ||
        r.feeders.some((f) => holdsMatch(f, focusId)),
    );
    if (i >= 0) setChosen(i);
  }, [focusId, focusN, ladder, third]);

  const loserLink = (lm: LadderMatch) => {
    const target = lm.loserTo;
    if (
      !target ||
      lm.match.isBye ||
      lm.match.player1?.isBye ||
      lm.match.player2?.isBye ||
      endedWithoutWinner(lm.match)
    )
      return undefined;
    const code = text.code(target.code);
    // A nested playoff is named by its places band; a stored match ID does not carry it, the round code does.
    const places = target.code.places;
    const where = places
      ? displayMatchCode(target.code.id)
        ? text.inPlayoff(places, code)
        : code
      : target.otherStageName
        ? t('inStage', { stage: text.stageName(target.otherStageName), code })
        : code;
    return { label: t('loserTo', { target: where }), matchId: target.matchId };
  };

  const scrollBy = (dir: -1 | 1) =>
    viewport.current?.scrollBy?.({ left: dir * step(), behavior: 'smooth' });

  const column = (round: LadderRound, i: number) => {
    const pairs: (typeof round.matches)[] = [];
    for (let k = 0; k < round.matches.length; k += 2) pairs.push(round.matches.slice(k, k + 2));
    const isNow = i === ladder.currentRound && !ladder.finished;
    const gameSlots = roundGameSlots(round);
    const withThird = !!third && i === lastIndex;
    return (
      <li
        key={round.roundId}
        data-round-index={i}
        data-selected={i === selected || undefined}
        data-now={isNow || undefined}
        className={cn(
          'flex shrink-0 flex-col px-1',
          // Print: the columns share the printable width instead of keeping their screen sizes.
          'print:w-auto! print:max-w-none! print:min-w-0! print:flex-1 print:shrink',
          !nested && PHONE_HIDE_UNSELECTED,
          tier.column,
          // Five or more game slots leave a name too little room: widen the column so the ladder scrolls sooner.
          // The wider tiers set their own min-w later in the cascade, so each repeats it.
          gameSlots >= 5 &&
            '@min-[640px]:min-w-72 @min-[860px]:min-w-72 @min-[920px]:min-w-72 @min-[1180px]:min-w-72 @min-[1380px]:min-w-72 print:min-w-0',
        )}
      >
        <button
          type="button"
          onClick={() => setChosen(i)}
          className={cn(
            'min-h-11 py-1.5 text-left text-xs font-semibold md:min-h-0',
            nested ? 'block' : '@max-[639px]:hidden',
            tier.header,
          )}
        >
          {text.roundLong(round.abbrev)}
          {round.live && (
            <span
              aria-hidden
              className="bg-danger ml-1 inline-block size-1.5 rounded-full align-middle"
            />
          )}
          <span className="text-muted block font-normal">
            {isNow && !nested
              ? t('now')
              : round.byes > 0
                ? t('playedWithByes', { played: round.played, byes: round.byes })
                : t('played', { played: round.played, total: round.total })}
          </span>
        </button>
        {/* In the tree every slot of a round is one equal grid row: a round's rows all take the
            tallest card's height, so a card with a wrapped name never pulls its pair's connector
            off centre and each next-round card stays level with the pair that feeds it. */}
        {/* With a 3rd/4th card the last column is three rows, empty / Final / card: the equal 1fr rows
            keep the Final centred on its connector and grow together if the card needs more room. */}
        <div
          className={cn(
            'relative flex flex-1 flex-col @min-[640px]:grid',
            withThird ? '@min-[640px]:grid-rows-[1fr_auto_1fr]' : '@min-[640px]:auto-rows-fr',
          )}
        >
          {pairs.map((pair, p) => (
            <div
              key={p}
              className={cn(
                'relative flex flex-1 flex-col @min-[640px]:grid @min-[640px]:auto-rows-fr',
                withThird && '@min-[640px]:row-start-2',
              )}
            >
              {pair.map((lm, k) => {
                const sent = sentWinner(lm.match);
                // The stub in is amber when one of the two feeding halves is, so the path never breaks.
                const at = p * 2 + k;
                const received =
                  i > 0 &&
                  ladder.rounds[i - 1].matches
                    .slice(at * 2, at * 2 + 2)
                    .some((f) => sentWinner(f.match));
                return (
                  <div key={lm.match.matchId} className="relative flex flex-1 items-center py-1.5 print:break-inside-avoid">
                    {i > 0 && (
                      <span
                        aria-hidden
                        data-path-line={received || undefined}
                        className={cn(
                          'absolute top-1/2 -left-[18px] w-[18px] border-t-[1.5px] @max-[639px]:hidden',
                          received ? 'border-bracket-path' : 'border-border-strong',
                        )}
                      />
                    )}
                    <MatchCard
                      match={lm.match}
                      code={text.code(lm.code)}
                      next={lm.next ? text.code(lm.next) : undefined}
                      loser={loserLink(lm)}
                      placeholders={[
                        lm.sources[0] && text.source(lm.sources[0]),
                        lm.sources[1] && text.source(lm.sources[1]),
                      ]}
                      playerHref={playerHref}
                      gameSlots={gameSlots}
                    />
                    {/* Each match draws its own half of the bracket line, so a winner's half can turn amber.
                        The last round has no next round to join, so it draws none. */}
                    {i < lastIndex && (
                    <span
                      aria-hidden
                      data-path-line={sent || undefined}
                      className={cn(
                        'absolute -right-[18px] w-[18px] @max-[639px]:hidden',
                        pair.length === 1
                          ? 'top-1/2 border-t-[1.5px]'
                          : k === 0
                            ? 'top-1/2 bottom-0 rounded-tr-md border-t-[1.5px] border-r-[1.5px]'
                            : 'top-0 bottom-1/2 rounded-br-md border-r-[1.5px] border-b-[1.5px]',
                        sent ? 'border-bracket-path' : 'border-border-strong',
                      )}
                    />
                    )}
                  </div>
                );
              })}
            </div>
          ))}
          {withThird && (
            <div
              data-third-place
              className="mt-3 py-1.5 @min-[640px]:row-start-3 @min-[640px]:mt-6 @min-[640px]:self-start"
            >
              <h4 className="text-muted pb-1.5 text-xs font-semibold tracking-wider uppercase">{third.places && !(third.places.from === 3 && third.places.to === 4) ? text.places(third.places) : t('playoffLabel')}</h4>
              {third.rounds[0].matches.map((lm) => (
                <MatchCard
                  key={lm.match.matchId}
                  match={lm.match}
                  code={text.code(lm.code)}
                  placeholders={[
                    lm.sources[0] && text.source(lm.sources[0]),
                    lm.sources[1] && text.source(lm.sources[1]),
                  ]}
                  playerHref={playerHref}
                  gameSlots={roundGameSlots(third.rounds[0])}
                />
              ))}
            </div>
          )}
        </div>
      </li>
    );
  };

  return (
    <div
      className="@container min-w-0 @min-[640px]:[&_[data-next-link]]:hidden"
      data-ladder={ladder.stage.stageId}
    >
      {!nested && (
        <div
          role="group"
          aria-label={t('rounds')}
          className="border-border mb-3 flex border-b @min-[640px]:hidden"
        >
          {ladder.rounds.map((round, i) => (
            <button
              key={round.roundId}
              type="button"
              aria-pressed={i === selected}
              onClick={() => setChosen(i)}
              className={cn(
                'min-h-11 flex-1 text-sm',
                i === selected
                  ? 'text-foreground font-semibold shadow-[inset_0_-2px_0_var(--primary)]'
                  : 'text-muted',
              )}
            >
              {text.round(round.abbrev)}
              {(round.live || (i === lastIndex && third?.rounds.some((r) => r.live))) && (
                <span
                  aria-hidden
                  className="bg-danger ml-1 inline-block size-1.5 rounded-full align-middle"
                />
              )}
            </button>
          ))}
        </div>
      )}

      <div className="relative">
        <button
          type="button"
          aria-label={t('previousRound')}
          onClick={() => scrollBy(-1)}
          className={cn(
            'bg-card border-border absolute top-1/2 -left-1.5 z-10 hidden print:hidden size-11 -translate-y-1/2 place-items-center rounded-full border md:size-8',
            edges.left && '@min-[640px]:grid',
          )}
        >
          <ChevronLeftIcon aria-hidden className="size-4" />
        </button>
        <button
          type="button"
          aria-label={t('nextRound')}
          onClick={() => scrollBy(1)}
          className={cn(
            'bg-card border-border absolute top-1/2 -right-1.5 z-10 hidden print:hidden size-11 -translate-y-1/2 place-items-center rounded-full border md:size-8',
            edges.right && '@min-[640px]:grid',
          )}
        >
          <ChevronRightIcon aria-hidden className="size-4" />
        </button>
        <div
          ref={viewport}
          onScroll={measure}
          className={cn(
            'overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden print:overflow-visible!',
            tier.viewport,
            edges.right && tier.mask,
          )}
        >
          <ol
            className={cn(
              'relative flex gap-7',
              !nested && '@max-[639px]:block @max-[639px]:w-full',
              'w-max print:w-full',
              tier.list,
              nested && '@max-[639px]:w-full @max-[639px]:flex-col @max-[639px]:gap-2',
            )}
          >
            {ladder.rounds.map(column)}
          </ol>
        </div>
      </div>

      {!nested && (
        <div aria-hidden className={cn('mt-1 hidden justify-center gap-1.5', tier.dots)}>
          {ladder.rounds.map((r, i) => (
            <i
              key={r.roundId}
              className={cn(
                'size-1.5 rounded-full',
                i >= edges.first && i < edges.first + 2 ? 'bg-secondary' : 'bg-border',
              )}
            />
          ))}
        </div>
      )}

      {!nested && (
        <div>
          {feederSections(ladder, third).map(({ round, roundIndex, feeders }) => (
            <section
              key={round.roundId}
              data-feeder-round={roundIndex}
              className={cn('mt-6 w-full space-y-4', roundIndex !== selected && '@max-[639px]:hidden')}
            >
              {feeders.map((f) => (
                <div key={f.stage.stageId} className="space-y-2">
                  <h4 className="text-sm font-semibold">
                    {text.stageName(f.stage.name)}
                    <span className="text-muted font-normal">
                      {' '}
                      · {t('fedBy', { round: text.round(round.abbrev) })}
                    </span>
                  </h4>
                  <LadderView ladder={f} nested playerHref={playerHref} />
                </div>
              ))}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
